
import { Component } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { COL, getTableName } from '@/worker/db/sqlHelpers';
import {
	PermissionsGroup,
	ValueText,
} from '@/store/models.def';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import moment from 'moment';
import gdbx from '@/store/modules/gdbx';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		TableFilterable,
		SelectFilter,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class SalesCustomerNoActivityTable extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['sales'];
	}

	public get monthList() {
		const list: object[] = [];
		for (let i = 1; i <= 24; i++) {
      if (i === 1) {
        list.push({
          name: i + ' month',
          value: i,
        });
      } else {
        list.push(
            {
              name: i + ' months',
              value: i,
            },
        );
      }
		}
		return list;
	}
	public get monthTextList() {
		const list: ValueText[] = [];
		for (let i = 0; i < 12; i++) {
			const object = { value: i, text: '' };
			object.text = moment().month(i).format('MMM');
			list.push(object);
		}
		return list;
	}

	public get aboveSelection() {
		return this.selectedAbove;
	}

	public get belowSelection() {
		return this.selectedBelow;
	}

	public get decimal() {
		return gdbx.numDecimal;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get dateFormatted() {
		return [this.currentMonth];
	}

	public get currentYear() {
		return moment(this.selectedAsOfDate).year();
	}

	public get currentMonth() {
		return moment()
			.year(this.currentYear)
			.month(this.selectedMonth)
			.endOf('month')
			.valueOf();
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedAgents,
			selectedStockItems,
			aboveSelection,
			belowSelection,
			selectedMonth,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAgents,
			selectedStockItems,
			aboveSelection,
			belowSelection,
			selectedMonth,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public tableItems: any[] = [];
	public tableFields: any[] = [];

	public selectedAbove: number = 1;
	public selectedBelow: number = 12;
	public selectedMonth: number = moment().month();

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.35);

		const queryHead = await this._ensureTablesLoaded(['sales', 'customerTrades'] as any[]);
		this.checkInterrupted(jobId);

		const salesTable = getTableName('sales');
		const arTable = getTableName('customerTrades');

		// Replicate dateAsOf timezone adjustment (browser → Malaysia GMT+8)
		let adjustedDate = this.currentMonth;
		const currentOffset = new Date().getTimezoneOffset();
		const targetOffset = -480;
		if (currentOffset !== targetOffset) {
			const difference = (currentOffset - targetOffset) * 60 * 1000;
			adjustedDate = adjustedDate - difference;
		}
		const dateAsOfFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) <= ${adjustedDate}`;

		// AR outstanding: sum LOCALOSAMOUNT per customer (no date filter)
		const outstandingQuery = `
			SELECT ${COL.CODE} as code,
				SUM(TRY_CAST("LOCALOSAMOUNT" AS DOUBLE)) as outstanding
			FROM ${arTable}
			GROUP BY ${COL.CODE}
		`;

		if (this.slPhModuleOnly) {
			const docTypes = this.includeDebitNote ? ['IV', 'CS', 'DN'] : ['IV', 'CS'];
			const docTypeFilter = ` AND ${COL.DOCTYPE} IN (${docTypes.map((t) => `'${t}'`).join(', ')})`;

			const lastInvoiceQuery = `
				WITH customer_dates AS (
					SELECT ${COL.CODE} as code,
						TRY_CAST(${COL.DOCDATE} AS BIGINT) as doc_date,
						SUM(TRY_CAST(${COL.AMOUNT} AS DOUBLE)) as date_amount,
						FIRST(${COL.AGENT}) as agent
					FROM ${salesTable}
					WHERE ${dateAsOfFilter}${docTypeFilter}
					GROUP BY ${COL.CODE}, TRY_CAST(${COL.DOCDATE} AS BIGINT)
				),
				customer_last AS (
					SELECT code, MAX(doc_date) as last_date
					FROM customer_dates
					GROUP BY code
				)
				SELECT cd.code, cd.agent, cd.doc_date as last_date, cd.date_amount as last_amount
				FROM customer_dates cd
				JOIN customer_last cl ON cd.code = cl.code AND cd.doc_date = cl.last_date
			`;

			this.generateLoadingText(0.35, 0.9);

			const [lastInvoiceRows, osRows] = await Promise.all([
				queryHead.rawQuery(lastInvoiceQuery),
				queryHead.rawQuery(outstandingQuery),
			]);
			this.checkInterrupted(jobId);

			const osMap = new Map<string, number>();
			for (const r of osRows) { osMap.set(String(r.code), Number(r.outstanding) || 0); }

			this.tableItems = this._buildTableItems(lastInvoiceRows, osMap);
		} else {
			const docTypes = this.includeDebitNote ? ['IV', 'DN'] : ['IV'];
			const docTypeFilter = ` AND ${COL.DOCTYPE} IN (${docTypes.map((t) => `'${t}'`).join(', ')})`;

			// AR mode uses LOCALAMOUNT instead of AMOUNT
			const arLastInvoiceQuery = `
				WITH customer_dates AS (
					SELECT ${COL.CODE} as code,
						TRY_CAST(${COL.DOCDATE} AS BIGINT) as doc_date,
						SUM(TRY_CAST(${COL.LOCALAMOUNT} AS DOUBLE)) as date_amount,
						FIRST(${COL.AGENT}) as agent
					FROM ${arTable}
					WHERE ${dateAsOfFilter}${docTypeFilter}
					GROUP BY ${COL.CODE}, TRY_CAST(${COL.DOCDATE} AS BIGINT)
				),
				customer_last AS (
					SELECT code, MAX(doc_date) as last_date
					FROM customer_dates
					GROUP BY code
				)
				SELECT cd.code, cd.agent, cd.doc_date as last_date, cd.date_amount as last_amount
				FROM customer_dates cd
				JOIN customer_last cl ON cd.code = cl.code AND cd.doc_date = cl.last_date
			`;

			this.generateLoadingText(0.35, 0.9);

			const [lastInvoiceRows, osRows] = await Promise.all([
				queryHead.rawQuery(arLastInvoiceQuery),
				queryHead.rawQuery(outstandingQuery),
			]);
			this.checkInterrupted(jobId);

			const osMap = new Map<string, number>();
			for (const r of osRows) { osMap.set(String(r.code), Number(r.outstanding) || 0); }

			this.tableItems = this._buildTableItems(lastInvoiceRows, osMap);
		}

		this.generateLoadingText(1);

		this.tableFields = [
			{ key: 'company', sortable: true },
			{
				key: 'agent',
				sortable: true,
			},
			{
				key: 'age',
				sortable: true,
				formatter: TableItemFormatter.basicInteger,
			},
			{
				key: 'last_invoice',
				sortable: true,
			},
			{
				key: 'last_amount',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'outstanding',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
		];

		this.saveHistory('tableItems', 'tableFields');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}

	private _buildTableItems(
		lastInvoiceRows: any[],
		osMap: Map<string, number>,
	): any[] {
		const result: any[] = [];

		for (const r of lastInvoiceRows) {
			const code = String(r.code);
			const lastDate = Number(r.last_date) || 0;
			const lastAmount = Number(r.last_amount) || 0;
			const agent = String(r.agent || '');

			const mmt = moment(lastDate);
			const diff = moment(this.currentMonth).diff(mmt, 'months');

			if (diff >= this.aboveSelection && diff <= this.belowSelection) {
				result.push({
					company_code: code,
					company: gdbx.customerNames[code] || code,
					agent,
					age: Math.ceil(moment.duration(this.currentMonth - lastDate).asDays()),
					last_invoice: moment(lastDate).format('DD/MM/YYYY'),
					last_amount: lastAmount,
					outstanding: osMap.get(code) || 0,
				});
			}
		}

		return result;
	}
}
